import { faSquarePlus } from "@fortawesome/free-solid-svg-icons";
import { isError } from "lodash-es";
import React from "react";
import SuccessBanner from "../../../components/Banner/SuccessBanner.tsx";
import { ErrorBanner } from "../../../components/Error/ErrorBanner.tsx";
import CreateButton from "../../../components/Modals/CreateButton.tsx";
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from "../../../components/Theme/dialog.tsx";
import { Heading } from "../../../components/Theme/heading.tsx";
import DisplayIfUserType from "../../../components/utils/DisplayIfUserType/DisplayIfUserType.tsx";
import { useCreateManualIssue } from "../../../hooks/createEntity.tsx";
import { type AssetRegisterItem, issueSeverityEnum } from "../../../kubb";
import { useRequiredSelectedCompanyId } from "../../../reducers/company.tsx";
import AssetRegisterSelectWrapper from "../../assetRegister/AssetRegisterListPage/AssetRegisterSelectWrapper.tsx";
import type { AssetListFilterParams } from "../../assetRegister/assets/list/AssetListFilterForm.tsx";
import ManualIssueForm, {
  type ManualIssueFormValues,
} from "./ManualIssueForm.tsx";

interface BulkManualIssueModelProps {
  initialFilters: AssetListFilterParams;
  refresh?: () => void;
}

export default function BulkManualIssueModal(
  props: BulkManualIssueModelProps,
): React.ReactElement {
  const [editing, setEditing] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState<Error | undefined>(undefined);
  const [lastRequestDate, setLastRequestDate] = React.useState(new Date());
  const [updatedNumber, setUpdatedNumber] = React.useState<number>(0);

  const create = useCreateManualIssue();
  const company = useRequiredSelectedCompanyId();
  const submit = async (values: ManualIssueFormValues): Promise<any> => {
    setSuccess(false);
    setError(undefined);
    try {
      const result = await create.update({
        ...values,
        asset_position_id: selected.map(
          (v) => v.asset_position.asset_position_id,
        ),
        company_id: company,
        severity: issueSeverityEnum.ERROR,
        start_date_time: null,
        end_date_time: null,
      });
      setSuccess(true);
      setLastRequestDate(new Date());
      setUpdatedNumber(selected.length);
      props.refresh?.();
      return result;
    } catch (error) {
      if (create.error) {
        setError(create.error);
      } else {
        if (isError(error)) {
          setError(error);
        } else {
          setError(Error("Unknown error"));
        }
      }
      throw error;
    }
  };

  const [selected, setSelected] = React.useState<AssetRegisterItem[]>([]);

  const close = () => {
    setEditing(false);
    setSelected([]);
    setSuccess(false);
    setError(undefined);
  };

  return (
    <DisplayIfUserType userTypes={"editor"}>
      <Dialog onClose={close} size={"7xl"} open={editing}>
        <DialogTitle>Bulk Create Manual Issues</DialogTitle>
        <DialogBody>
          <Heading level={4}>Select Asset Positions</Heading>
          <AssetRegisterSelectWrapper
            initialValues={{ ...props.initialFilters, max_items: 1000 }}
            setValues={setSelected}
            lastUpdateDate={lastRequestDate}
          />
          <Heading level={4} className={"pt-2"}>
            New Issue
          </Heading>
          <ManualIssueForm
            submit={submit}
            cancel={close}
            invalid={selected.length === 0}
            count={selected.length}
          />
          {success && (
            <SuccessBanner
              title={`${updatedNumber} issue${updatedNumber !== 1 ? "s were" : " was"} created.`}
            />
          )}
          <ErrorBanner error={error} />
        </DialogBody>
      </Dialog>
      <CreateButton
        onClick={() => setEditing(true)}
        labelText={"Bulk Create Manual Issues"}
        labelIcon={faSquarePlus}
      />
    </DisplayIfUserType>
  );
}
